import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import { useDistanceTypeServiceV1BatchCreateDistanceTypeMutation } from "_services/modules/distance-type";
import { DistanceCondition } from "_services/modules/distanceType";
import { parseFormattedNumber, yupNumTransform } from "_helper/formatters";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";

interface DistanceTypeItem {
  distanceTypeName: string;
  distanceCondition: DistanceCondition;
  distanceValue: number | string;
  distanceMin: number | string;
  distanceMax: number | string;
  description: string;
  status: string;
}

export interface AddDistanceTypeFormValues {
  distanceTypeName: string;
  distanceCondition: DistanceCondition;
  distanceValue: number | string;
  distanceMin: number | string;
  distanceMax: number | string;
  description: string;
  status: string;
  items: DistanceTypeItem[];
}

const schema = yup.object({
  distanceTypeName: yup.string().required("Distance Type Name is required").max(100, "Max 100 characters"),
  distanceCondition: yup.string().oneOf(["below", "between", "above"]).required("Distance Condition is required"),
  distanceValue: yup.mixed().when("distanceCondition", {
    is: (val: string) => val === "below" || val === "above",
    then: () => yup.number().transform(yupNumTransform).typeError("Must be a number").required("Distance value is required").positive("Must be positive"),
    otherwise: () => yup.mixed().notRequired(),
  }),
  distanceMin: yup.mixed().when("distanceCondition", {
    is: "between",
    then: () => yup.number().transform(yupNumTransform).typeError("Must be a number").required("Min value is required").min(0, "Must be >= 0"),
    otherwise: () => yup.mixed().notRequired(),
  }),
  distanceMax: yup.mixed().when("distanceCondition", {
    is: "between",
    then: () => yup.number().transform(yupNumTransform).typeError("Must be a number").required("Max value is required").positive("Must be positive")
      .when("distanceMin", (distanceMin, schema) => {
        const minVal = Array.isArray(distanceMin) ? distanceMin[0] : distanceMin;
        if (typeof minVal === "number" && !isNaN(minVal)) {
          return schema.moreThan(minVal, "Max must be greater than Min");
        }
        return schema;
      }),
    otherwise: () => yup.mixed().notRequired(),
  }),
  description: yup.string().max(500, "Max 500 characters").default(""),
  status: yup.string().required("Status is required"),
  items: yup.array().of(
    yup.object({
      distanceTypeName: yup.string().required(),
      distanceCondition: yup.string().required(),
      distanceValue: yup.mixed(),
      distanceMin: yup.mixed(),
      distanceMax: yup.mixed(),
      description: yup.string().default(""),
      status: yup.string().required(),
    })
  ).required(),
}).required();

const useAddDistanceTypeForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("distanceType");

  const [batchCreateDistanceType] = useDistanceTypeServiceV1BatchCreateDistanceTypeMutation();

  const {
    control,
    register,
    trigger,
    getValues,
    setValue,
    watch,
    setError,
    formState: { errors },
  } = useForm<AddDistanceTypeFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema) as never,
    defaultValues: {
      distanceTypeName: "",
      distanceCondition: "below",
      distanceValue: "",
      distanceMin: "",
      distanceMax: "",
      description: "",
      status: "active",
      items: [],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "items",
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const distanceCondition = watch("distanceCondition");

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-add"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  const getDistanceDisplayText = (item: DistanceTypeItem): string => {
    if (item.distanceCondition === "below") {
      const parseNum = parseFormattedNumber;
      return `< ${(parseNum(item.distanceValue) || 0).toLocaleString()} Km`;
    }
    if (item.distanceCondition === "between") {
      const parseNum = parseFormattedNumber;
      return `${(parseNum(item.distanceMin) || 0).toLocaleString()} Km - ${(parseNum(item.distanceMax) || 0).toLocaleString()} Km`;
    }
    if (item.distanceCondition === "above") {
      const parseNum = parseFormattedNumber;
      return `> ${(parseNum(item.distanceValue) || 0).toLocaleString()} Km`;
    }
    return "-";
  };

  const handleAddToList = async () => {
    const fieldsToValidate: (keyof AddDistanceTypeFormValues)[] = [
      "distanceTypeName",
      "distanceCondition",
      "status",
    ];

    if (distanceCondition === "between") {
      fieldsToValidate.push("distanceMin", "distanceMax");
    } else {
      fieldsToValidate.push("distanceValue");
    }

    const isValid = await trigger(fieldsToValidate);
    if (!isValid) return;

    const values = getValues();

    const isDuplicate = fields.some(
      (item, index) =>
        item.distanceTypeName.toLowerCase() === values.distanceTypeName.toLowerCase() &&
        index !== editingIndex
    );

    if (isDuplicate) {
      setError("distanceTypeName", {
        type: "validate",
        message: t("error-duplicate-value"),
      });
      return;
    }

    const normalizeNum = (val: number | string) =>
      typeof val === "string" && val !== "" ? parseFormattedNumber(val) : val;

    const newItem: DistanceTypeItem = {
      distanceTypeName: values.distanceTypeName,
      distanceCondition: values.distanceCondition,
      distanceValue: values.distanceCondition !== "between" ? normalizeNum(values.distanceValue) : "",
      distanceMin: values.distanceCondition === "between" ? normalizeNum(values.distanceMin) : "",
      distanceMax: values.distanceCondition === "between" ? normalizeNum(values.distanceMax) : "",
      description: values.description,
      status: values.status,
    };

    if (editingIndex !== null) {
      update(editingIndex, newItem);
      setEditingIndex(null);
    } else {
      append(newItem);
    }

    resetForm();
  };

  const resetForm = () => {
    setValue("distanceTypeName", "");
    setValue("distanceCondition", "below");
    setValue("distanceValue", "");
    setValue("distanceMin", "");
    setValue("distanceMax", "");
    setValue("description", "");
    setValue("status", "active");
  };

  const handleEditItem = (index: number) => {
    const item = fields[index];
    setEditingIndex(index);
    setValue("distanceTypeName", item.distanceTypeName);
    setValue("distanceCondition", item.distanceCondition);
    setValue("distanceValue", item.distanceValue);
    setValue("distanceMin", item.distanceMin);
    setValue("distanceMax", item.distanceMax);
    setValue("description", item.description);
    setValue("status", item.status);
  };

  const handleDeleteItem = (index: number) => {
    remove(index);
    if (editingIndex === index) {
      setEditingIndex(null);
      resetForm();
    } else if (editingIndex !== null && index < editingIndex) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const handleDiscardChanges = () => {
    setEditingIndex(null);
    resetForm();
  };

  const handleSave = async () => {
    try {
      const payload = {
        distance_types: fields.map((f) => ({
          distance_type_name: f.distanceTypeName,
          distance_condition: f.distanceCondition,
          distance_min: f.distanceCondition === "between" ? Number(f.distanceMin) : (f.distanceCondition === "below" ? 0 : Number(f.distanceValue)),
          distance_max: f.distanceCondition === "between" ? Number(f.distanceMax) : (f.distanceCondition === "below" ? Number(f.distanceValue) : 0),
          description: f.description,
          status: f.status,
        })),
      };
      await batchCreateDistanceType({ distanceTypeV1BatchCreateDistanceTypeReq: payload }).unwrap();
      toast.success(t("toast-create-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error: any) {
      errorHandler(error);
      setShowSaveModal(false);
    }
  };

  const handleCancel = () => {
    if (fields.length > 0) {
      setShowCancelModal(true);
    } else {
      navigate(-1);
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate(-1);
  };

  return {
    control,
    register,
    errors,
    fields,
    editingIndex,
    distanceCondition,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    handleAddToList,
    handleEditItem,
    handleDeleteItem,
    handleDiscardChanges,
    handleSave,
    handleCancel,
    handleConfirmCancel,
    getDistanceDisplayText,
    t,
  };
};

export default useAddDistanceTypeForm;
